import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@/components/ui/carousel";
import {
    Calendar, Receipt, Users, Crown, BarChart3, MessageSquare, TrendingUp,
    Shield, MapPin,
} from "lucide-react";
import { bookingUrl, brand } from "@/lib/site";
import BrandLogo from "@/components/BrandLogo";
import LandingHeader from "@/components/LandingHeader";
import { api } from "@/lib/api";

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

const EYEBROW = "font-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] sm:text-xs";

/** Standard landing layout rhythm — consistent, not too loose or tight */
const PAGE_X = "px-5 sm:px-6";
const SECTION_Y = "py-16 sm:py-20";
const BLOCK_GAP = "mt-10";
const CARD_GAP = "gap-5";

function SectionHeader({ eyebrow, title, subtitle, light = false, center = false }) {
    return (
        <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
            <p className={EYEBROW} style={{ color: light ? "#D4AF37" : "#C9A227" }}>
                {eyebrow}
            </p>
            <h2
                className={`mt-2 font-serif text-3xl font-medium tracking-tight sm:text-4xl ${
                    light ? "text-white" : "text-[#4A0E17]"
                }`}
            >
                {title}
            </h2>
            {subtitle && (
                <p className={`mt-3 text-base leading-relaxed ${light ? "text-white/75" : "text-[#5C4033]/85"}`}>
                    {subtitle}
                </p>
            )}
            <GoldDivider className={`mt-4 ${center ? "mx-auto" : ""}`} light={light} />
        </div>
    );
}

function GoldDivider({ className = "max-w-[200px]", light = false }) {
    const gem = light ? "#FFE9A8" : "#C9A227";
    const line = light
        ? "h-px flex-1 bg-gradient-to-r from-[#D4AF37]/25 via-[#D4AF37] to-[#D4AF37]/25"
        : "h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/80 to-transparent";
    return (
        <div className={`flex items-center ${className}`}>
            <div className={line} />
            <span className="px-2.5 text-[10px] leading-none" style={{ color: gem }}>◆</span>
            <div className={line} />
        </div>
    );
}

const HERO =
    "https://assets.kerzner.com/api/public/content/2130c78b25dd435986ed528819559ed6?v=43dca329&t=w576";
const HERO_VISUAL = "/login-hero.png";
const HOW_IT_WORKS_IMG =
    "https://studiomanager.asia/wp-content/uploads/2024/11/Spa-CRM-Software-Mobile.jpg";

/** High-quality hero collage — portrait + square, staggered like reference */
const HERO_IMG_SPA =
    "https://studieo7.in/wp-content/uploads/2022/12/girl-111.png";
const HERO_IMG_SALON =
    "https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=800&q=85";
const TESTIMONIAL_IMG_SPA =
    "https://images.unsplash.com/photo-1516975080664-ed2fc6a32937?auto=format&fit=crop&w=800&q=85";

function HeroPhotoFrame({ src, alt, aspectClass, position = "center top", zoom = 1.12 }) {
    return (
        <div
            className={`relative overflow-hidden rounded-2xl border border-[#E8E4DE] bg-neutral-500 shadow-[0_12px_32px_rgba(45,41,38,0.1)] ${aspectClass}`}
        >
            <div
                className="absolute inset-x-0 top-0 z-10 h-[2px] bg-gradient-to-r from-[#4A0E17] via-[#D4AF37] to-[#4A0E17]/75"
                aria-hidden
            />
            <img
                src={src}
                alt={alt}
                loading="eager"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover"
                style={{
                    objectPosition: position,
                    transform: `scale(${zoom})`,
                }}
            />
        </div>
    );
}

function HeroImageCollage() {
    return (
        <div className="relative mx-auto w-full max-w-[340px] sm:max-w-[380px] md:max-w-none">
            <div
                className="pointer-events-none absolute -inset-3 rounded-[1.5rem] opacity-50"
                style={{ background: "radial-gradient(circle at 40% 30%, rgba(212,175,55,0.18) 0%, transparent 70%)" }}
                aria-hidden
            />
            <div className="relative grid grid-cols-2 items-start gap-3 sm:gap-4">
                <HeroPhotoFrame
                    src={HERO_IMG_SPA}
                    alt="Salon client with beautiful hair"
                    aspectClass="aspect-[3/4] w-full"
                    position="center 18%"
                    zoom={1.18}
                />
                <div className="mt-8 flex flex-col gap-3 sm:mt-12 sm:gap-4">
                    <HeroPhotoFrame
                        src={HERO_IMG_SALON}
                        alt="Professional hair styling"
                        aspectClass="aspect-square w-full"
                        position="center"
                        zoom={1.05}
                    />
                    <HeroEarningsCard />
                </div>
            </div>
        </div>
    );
}

const howSteps = [
    {
        title: "Sign up",
        description: "Tell us about your salon — we pre-load services, GST and a starter price list.",
    },
    {
        title: "Invite your team",
        description: "Share a link with stylists. Everyone books on the same calendar.",
    },
    {
        title: "Share your booking page",
        description: "Drop the link in Instagram bio. Customers book 24×7.",
    },
    {
        title: "Watch revenue grow",
        description: "Daily KPIs on your phone. Nudges for retention and rebooking.",
    },
];

function HowSideImage() {
    return (
        <div className="relative mx-auto w-full max-w-[240px] sm:max-w-[260px] lg:max-w-none">
            <div className="relative overflow-hidden rounded-xl border border-white/10 shadow-[0_16px_40px_rgba(0,0,0,0.25)]">
                <div className="absolute inset-x-0 top-0 z-10 h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37]/80 to-transparent" aria-hidden />
                <img
                    src={HOW_IT_WORKS_IMG}
                    alt="Salon owner using spa CRM on tablet"
                    className="aspect-[4/3] max-h-[240px] w-full object-cover object-center sm:max-h-[260px] lg:max-h-[300px]"
                />
            </div>
        </div>
    );
}

function HowStepRow({ index, title, description, isLast }) {
    return (
        <li className={`flex gap-3.5 py-3 sm:py-3.5 ${isLast ? "" : "border-b border-white/10"}`}>
            <span className="w-7 shrink-0 font-serif text-xl font-medium leading-none text-[#D4AF37] sm:text-2xl">
                {String(index).padStart(2, "0")}
            </span>
            <div className="min-w-0 pt-0.5">
                <h3 className="font-serif text-base text-white sm:text-lg">{title}</h3>
                <p className="mt-1 text-sm leading-snug text-white/70">{description}</p>
            </div>
        </li>
    );
}

function HeroEarningsCard() {
    return (
        <div
            className="relative w-full overflow-hidden rounded-xl border border-[#E8E4DE] bg-white px-3 py-2.5 shadow-[0_6px_20px_rgba(45,41,38,0.08)]"
            style={{ background: "linear-gradient(160deg, #FFFEF9 0%, #FFFFFF 100%)" }}
        >
                <div
                    className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-[#4A0E17]/90 via-[#D4AF37] to-[#4A0E17]/50"
                    aria-hidden
                />
                <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#8A8178]">
                    Total earnings
                </p>
                <p className="mt-0.5 font-serif text-[1.35rem] font-semibold leading-none tabular-nums text-[#4A0E17]">
                    ₹42,800
                </p>
                <p className="mt-1.5 flex items-center gap-1 text-[10px] font-bold text-[#2D7A4F]">
                    <TrendingUp className="size-3 shrink-0" strokeWidth={2.5} />
                    <span>↑ 25% vs last month</span>
                </p>
        </div>
    );
}

const testimonials = [
    {
        id: "siddhi",
        image: HERO_IMG_SPA,
        imagePosition: "center 15%",
        imageZoom: 1.15,
        badge: "25% lift in average check sizes",
        quote:
            "We stopped juggling registers, WhatsApp, and spreadsheets. One dashboard runs our calendar, GST billing, and staff pay — and I check revenue from my phone every morning.",
        name: "Siddhi Khaire",
        role: "Owner, Siddhi's Salon & Spa · Pune",
    },
    {
        id: "priya",
        image: HERO_IMG_SALON,
        imagePosition: "center 30%",
        imageZoom: 1.08,
        badge: "40% fewer no-shows",
        quote:
            "Automated booking reminders changed everything. Our chairs stay full, walk-ins are handled smoothly, and the front desk finally has breathing room on busy Saturdays.",
        name: "Priya Mehta",
        role: "Owner, Luxe Hair Studio · Mumbai",
    },
    {
        id: "ananya",
        image: HERO_VISUAL,
        imagePosition: "72% center",
        imageZoom: 1.05,
        badge: "2× faster checkout",
        quote:
            "GST invoices, UPI, and memberships in one tap — clients love how quick checkout feels. We closed month-end in hours instead of a full weekend on Excel.",
        name: "Ananya Roy",
        role: "Co-founder, Glow & Grace Spa · Bengaluru",
    },
    {
        id: "neha",
        image: "/premium-salon.png",
        imagePosition: "center",
        imageZoom: 1.1,
        badge: "18% growth in repeat visits",
        quote:
            "Membership plans and birthday reminders brought clients back on their own. I can see who hasn't visited in 30 days and send a nudge before they drift away.",
        name: "Neha Desai",
        role: "Owner, Studio 7 Unisex Salon · Ahmedabad",
    },
    {
        id: "kavya",
        image: TESTIMONIAL_IMG_SPA,
        imagePosition: "center 20%",
        imageZoom: 1.1,
        badge: "Staff payroll in one click",
        quote:
            "Commission, tips, and attendance used to take me two evenings every month. Now payroll is done before I leave the salon — my stylists trust the numbers.",
        name: "Kavya Nair",
        role: "Owner, Bliss Day Spa · Kochi",
    },
];

function TestimonialAvatar({ image, alt, imagePosition, imageZoom }) {
    return (
        <div className="relative size-36 overflow-hidden rounded-full border-4 border-[#FAF9F6] bg-neutral-500 shadow-[0_12px_32px_rgba(45,41,38,0.12)] ring-2 ring-[#D4AF37]/25 sm:size-44">
            <img
                src={image}
                alt={alt}
                className="absolute inset-0 h-full w-full object-cover"
                style={{
                    objectPosition: imagePosition,
                    transform: `scale(${imageZoom})`,
                }}
            />
        </div>
    );
}

function TestimonialSlide({ badge, quote, name, role, image, imagePosition, imageZoom }) {
    return (
        <div className="flex flex-col items-center gap-8 md:flex-row md:items-center md:gap-12 lg:gap-16">
            <div className="shrink-0">
                <TestimonialAvatar
                    image={image}
                    alt={name}
                    imagePosition={imagePosition}
                    imageZoom={imageZoom}
                />
            </div>
            <div className="min-w-0 flex-1 text-center md:text-left">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#E8F5EC] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[#2D7A4F]">
                    <TrendingUp className="size-3.5 shrink-0" strokeWidth={2.5} />
                    {badge}
                </span>
                <blockquote className="mt-5 font-serif text-2xl font-medium leading-snug tracking-tight text-[#4A0E17] sm:text-[1.75rem] sm:leading-snug lg:text-3xl">
                    <span className="text-[#D4AF37]/90">&ldquo;</span>
                    {quote}
                    <span className="text-[#D4AF37]/90">&rdquo;</span>
                </blockquote>
                <p
                    className="mt-6 text-3xl leading-none text-[#4A0E17] sm:text-[2rem]"
                    style={{ fontFamily: "'Segoe Script', 'Brush Script MT', cursive" }}
                >
                    {name}
                </p>
                <p className="mt-2 text-sm font-medium text-[#8A8178]">{role}</p>
            </div>
        </div>
    );
}

function TestimonialSection() {
    const [api, setApi] = useState(null);
    const [current, setCurrent] = useState(0);

    useEffect(() => {
        if (!api) return undefined;
        const sync = () => setCurrent(api.selectedScrollSnap());
        sync();
        api.on("select", sync);
        return () => api.off("select", sync);
    }, [api]);

    useEffect(() => {
        if (!api) return undefined;
        const timer = setInterval(() => api.scrollNext(), 7000);
        return () => clearInterval(timer);
    }, [api]);

    return (
        <section className={`border-t border-[#E8E4DE]/60 bg-white ${SECTION_Y}`}>
            <div className={`mx-auto max-w-5xl ${PAGE_X}`}>
                <SectionHeader
                    center
                    eyebrow="Salon stories"
                    title="Trusted by owners across India"
                    subtitle="Real salons. Real numbers. See how teams run smoother after switching."
                />
                <div className={`relative ${BLOCK_GAP}`}>
                    <Carousel
                        setApi={setApi}
                        opts={{ loop: true, align: "start" }}
                        className="w-full"
                    >
                        <CarouselContent className="-ml-0">
                            {testimonials.map((item) => (
                                <CarouselItem key={item.id} className="pl-0">
                                    <TestimonialSlide {...item} />
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                        <CarouselPrevious
                            className="left-0 top-[5.5rem] h-9 w-9 -translate-y-0 border-[#E8E4DE] bg-white text-[#4A0E17] shadow-md hover:bg-[#FAF9F6] disabled:opacity-40 md:top-1/2 md:-left-3 md:-translate-y-1/2 lg:-left-12"
                        />
                        <CarouselNext
                            className="right-0 top-[5.5rem] h-9 w-9 -translate-y-0 border-[#E8E4DE] bg-white text-[#4A0E17] shadow-md hover:bg-[#FAF9F6] disabled:opacity-40 md:top-1/2 md:-right-3 md:-translate-y-1/2 lg:-right-12"
                        />
                    </Carousel>
                    <div className="mt-8 flex items-center justify-center gap-2">
                        {testimonials.map((item, index) => (
                            <button
                                key={item.id}
                                type="button"
                                aria-label={`Go to testimonial ${index + 1}`}
                                aria-current={current === index ? "true" : undefined}
                                onClick={() => api?.scrollTo(index)}
                                className={`h-2 rounded-full transition-all duration-300 ${
                                    current === index
                                        ? "w-6 bg-[#4A0E17]"
                                        : "w-2 bg-[#D4AF37]/45 hover:bg-[#D4AF37]/70"
                                }`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

const features = [
    { i: Calendar, t: "Smart Calendar", d: "Drag-and-drop bookings, conflict-free scheduling, walk-in queue.", tone: "#3B82C4", wash: "#E0EDFA" },
    { i: Receipt, t: "GST Billing", d: "1-tap invoicing with CGST/SGST, UPI, and Razorpay payments.", tone: "#C84B31", wash: "#FCE8E4" },
    { i: Users, t: "Customer CRM", d: "Service history, preferences, and birthday reminders in one place.", tone: "#D9468F", wash: "#FBE4EF" },
    { i: Crown, t: "Memberships", d: "Silver, Gold, Platinum prepaid plans that drive retention.", tone: "#C9A227", wash: "#FDF3DC" },
    { i: BarChart3, t: "Live Reports", d: "Daily revenue, top services, and staff — on your phone.", tone: "#2D9B6E", wash: "#DCEEDC" },
    { i: MessageSquare, t: "AI Assistant", d: "Aaria answers customers and nudges rebookings around the clock.", tone: "#7C5CBF", wash: "#EDE8F8" },
];

function FeatureCard({ icon: Icon, title, description, tone, wash }) {
    return (
        <article
            className="group relative isolate overflow-hidden rounded-2xl border border-[#E8E4DE]/90 bg-white p-6 transition-[transform,box-shadow,border-color] duration-300 ease-out motion-reduce:transition-none hover:-translate-y-1 hover:border-[#D4AF37]/45 hover:shadow-[0_16px_40px_rgba(74,14,23,0.07)] motion-reduce:hover:translate-y-0"
        >
            <div
                className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                style={{
                    background: `radial-gradient(ellipse 80% 60% at 100% 0%, ${wash}88 0%, transparent 70%)`,
                }}
                aria-hidden
            />
            <div
                className="pointer-events-none absolute inset-x-0 top-0 h-px origin-left scale-x-[0.35] bg-gradient-to-r from-[#4A0E17] via-[#D4AF37] to-transparent transition-transform duration-500 ease-out group-hover:scale-x-100"
                aria-hidden
            />

            <div className="relative flex items-start gap-4">
                <div
                    className="flex size-11 shrink-0 items-center justify-center rounded-xl ring-1 ring-[#E8E4DE]/60 transition-all duration-300 ease-out group-hover:scale-105 group-hover:shadow-[0_6px_16px_rgba(45,41,38,0.08)] group-hover:ring-transparent"
                    style={{ backgroundColor: wash, color: tone }}
                >
                    <Icon
                        className="size-[18px] transition-transform duration-300 group-hover:scale-110"
                        strokeWidth={2.25}
                    />
                </div>
                <div className="min-w-0 pt-0.5">
                    <h3 className="font-serif text-xl font-medium tracking-tight text-[#4A0E17] transition-colors duration-300 group-hover:text-[#3D2817]">
                        {title}
                    </h3>
                    <p className="mt-2 text-[13px] leading-relaxed text-[#8A8178] transition-colors duration-300 group-hover:text-[#5C4033]/90">
                        {description}
                    </p>
                </div>
            </div>

            <div
                className="pointer-events-none absolute bottom-4 right-4 size-1.5 rounded-full opacity-0 transition-all duration-300 group-hover:opacity-100"
                style={{ backgroundColor: tone, boxShadow: `0 0 10px ${tone}66` }}
                aria-hidden
            />
        </article>
    );
}

const valuePillars = [
    {
        image: HERO_IMG_SALON,
        imagePosition: "center",
        imageZoom: 1.05,
        title: "There for the floor",
        description:
            "Built with owners who live at the chair, counter, and calendar — not buried in registers and WhatsApp threads.",
    },
    {
        image: HERO_VISUAL,
        imagePosition: "72% center",
        imageZoom: 1.04,
        title: "There for your team",
        description:
            "One shared calendar for stylists and front desk. Walk-ins, bookings, and payments stay in sync all day.",
    },
    {
        image: "/premium-salon.png",
        imagePosition: "center",
        imageZoom: 1.08,
        title: "There for tomorrow",
        description:
            "Memberships, reminders, and daily KPIs help you fill chairs, lift check sizes, and win clients back.",
    },
];

function ValuePillarCard({ image, imagePosition, imageZoom, title, description }) {
    return (
        <article className="text-center">
            <div className="relative overflow-hidden rounded-xl border border-[#E8E4DE] bg-neutral-500 shadow-[0_8px_24px_rgba(45,41,38,0.06)]">
                <div
                    className="absolute inset-x-0 top-0 z-10 h-[2px] bg-gradient-to-r from-[#4A0E17]/90 via-[#D4AF37] to-[#4A0E17]/50"
                    aria-hidden
                />
                <img
                    src={image}
                    alt={title}
                    loading="lazy"
                    decoding="async"
                    className="aspect-[4/3] w-full max-h-[180px] object-cover sm:max-h-[200px]"
                    style={{
                        objectPosition: imagePosition,
                        transform: `scale(${imageZoom})`,
                    }}
                />
            </div>
            <h3 className="mt-4 font-serif text-lg font-medium tracking-tight text-[#4A0E17] sm:text-xl">
                {title}
            </h3>
            <p className="mx-auto mt-2 max-w-[280px] text-sm leading-relaxed text-[#5C4033]/85">
                {description}
            </p>
        </article>
    );
}

function SalonValueSection() {
    return (
        <section className="border-t border-[#E8E4DE]/60 bg-white py-12 sm:py-14">
            <div className={`mx-auto max-w-7xl ${PAGE_X}`}>
                <SectionHeader
                    center
                    eyebrow="Built on the ground"
                    title="Tech for real-world salons"
                    subtitle="Designed alongside salon owners and stylists — so bookings, billing, and growth work the way your team actually runs the floor."
                />
                <div className={`mt-8 grid ${CARD_GAP} sm:grid-cols-3`}>
                    {valuePillars.map((pillar) => (
                        <ValuePillarCard key={pillar.title} {...pillar} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function LandingFooter() {
    const year = new Date().getFullYear();

    const productLinks = [
        { label: "Features", href: "#features" },
        { label: "How it works", href: "#how" },
        { label: "Pricing", href: "#pricing" },
        { label: "Blog", to: "/blog" },
    ];

    const accountLinks = [
        { label: "Start free trial", to: "/register" },
        { label: "Sign in", to: "/login" },
    ];

    const trustItems = [
        { icon: Receipt, label: "GST-compliant billing" },
        { icon: Shield, label: "DPDP-ready privacy" },
        { icon: MapPin, label: "Built in India" },
    ];

    return (
        <footer className="border-t border-[#E8E4DE]/60" data-testid="landing-footer">
            <div
                className="relative overflow-hidden"
                style={{ background: "linear-gradient(165deg, #4A0E17 0%, #3a0b12 100%)" }}
            >
                <div
                    className="pointer-events-none absolute inset-0 opacity-[0.06]"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 15% 0%, #D4AF37 0%, transparent 45%), radial-gradient(circle at 85% 100%, #C84B31 0%, transparent 40%)",
                    }}
                    aria-hidden
                />
                <div className={`relative z-10 mx-auto max-w-7xl ${PAGE_X} py-14 sm:py-16`}>
                    <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-12 lg:gap-8">
                        <div className="lg:col-span-5">
                            <BrandLogo
                                size={34}
                                textClass="font-serif text-xl font-semibold text-white"
                                accentClass="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#D4AF37]"
                            />
                            <p className="mt-4 max-w-sm text-sm leading-relaxed">
                                <span className="block font-semibold text-[#FFE9A8]">
                                    {brand.tagline}
                                </span>
                                <span className="mt-2 block text-[#F5F0E8]">
                                    Bookings, GST billing, memberships, and reports — everything your salon needs in one place.
                                </span>
                            </p>
                            <GoldDivider light className="mt-5 max-w-[160px]" />
                            <a
                                href={bookingUrl("demo-salon")}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-5 inline-flex text-sm font-semibold text-[#D4AF37] transition-colors hover:text-[#FFE9A8]"
                            >
                                See a live booking page →
                            </a>
                        </div>

                        <div className="lg:col-span-2">
                            <p className={EYEBROW} style={{ color: "#D4AF37" }}>Product</p>
                            <ul className="mt-4 space-y-2.5">
                                {productLinks.map(({ label, href, to }) => (
                                    <li key={href || to}>
                                        {to ? (
                                            <Link
                                                to={to}
                                                className="text-sm font-medium text-white/75 transition-colors hover:text-white"
                                            >
                                                {label}
                                            </Link>
                                        ) : (
                                            <a
                                                href={href}
                                                className="text-sm font-medium text-white/75 transition-colors hover:text-white"
                                            >
                                                {label}
                                            </a>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="lg:col-span-2">
                            <p className={EYEBROW} style={{ color: "#D4AF37" }}>Get started</p>
                            <ul className="mt-4 space-y-2.5">
                                {accountLinks.map(({ label, to }) => (
                                    <li key={to}>
                                        <Link
                                            to={to}
                                            className="text-sm font-medium text-white/75 transition-colors hover:text-white"
                                        >
                                            {label}
                                        </Link>
                                    </li>
                                ))}
                                <li>
                                    <a
                                        href={bookingUrl("demo-salon")}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-sm font-medium text-white/75 transition-colors hover:text-white"
                                    >
                                        Demo booking page
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <div className="lg:col-span-3">
                            <p className={EYEBROW} style={{ color: "#D4AF37" }}>Why salons choose us</p>
                            <ul className="mt-4 space-y-3">
                                {trustItems.map(({ icon: Icon, label }) => (
                                    <li key={label} className="flex items-center gap-2.5 text-sm font-medium text-white/80">
                                        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5">
                                            <Icon className="size-3.5 text-[#D4AF37]" strokeWidth={2.25} />
                                        </span>
                                        {label}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="relative z-10 border-t border-white/10">
                    <div className={`mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 ${PAGE_X} py-5 sm:flex-row`}>
                        <p className="text-center text-xs font-medium text-white/55 sm:text-left">
                            © {year} {brand.name}. All rights reserved.
                        </p>
                        <p className="text-center text-xs font-medium text-white/45">
                            Salon &amp; spa management · Secure payments · No lock-in
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
}

export default function Landing() {
    const [plans, setPlans] = useState([]);
    const [trialDays, setTrialDays] = useState(14);
    const [plansLoading, setPlansLoading] = useState(true);
    const [cycle, setCycle] = useState("monthly");

    useEffect(() => {
        api.get("/public/plans")
            .then((r) => {
                setPlans(r.data?.plans || []);
                setTrialDays(r.data?.default_trial_days || 14);
            })
            .catch(() => setPlans([]))
            .finally(() => setPlansLoading(false));
    }, []);

    return (
        <div className="min-h-screen bg-[#FAF9F6] font-['Outfit',system-ui,sans-serif]">
            <LandingHeader />

            {/* Hero — same two-column layout */}
            <section className="relative overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-60"
                    style={{ backgroundImage: `url(${HERO})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-[#FAF9F6]/50 via-[#FAF9F6]/80 to-[#FAF9F6]" />
                <div
                    className="pointer-events-none absolute -right-20 top-10 size-72 rounded-full opacity-50"
                    style={{ background: "radial-gradient(circle, rgba(212,175,55,0.14) 0%, transparent 70%)" }}
                    aria-hidden
                />
                <div className={`relative mx-auto grid max-w-7xl items-center ${CARD_GAP} ${PAGE_X} pb-16 pt-14 sm:pb-20 sm:pt-16 md:grid-cols-12 lg:pt-20`}>
                    <div className="md:col-span-7">
                        <p className={EYEBROW} style={{ color: "#C9A227" }}>
                            Salon &amp; spa platform
                        </p>
                        <h1 className="mt-3 font-serif text-5xl font-medium leading-[1.05] tracking-tight text-[#4A0E17] sm:text-6xl lg:text-7xl">
                            Everything you need to run your salon,{" "}
                            <span className="italic text-[#C84B31]">beautifully.</span>
                        </h1>
                        <GoldDivider className="mt-5 max-w-[220px]" />
                        <p className="mt-6 max-w-xl text-lg leading-relaxed text-[#5C4033]/90">
                            Manage bookings, billing, staff, clients and growth — all from one smart platform.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/register" data-testid="hero-cta-trial">
                                <Button
                                    size="lg"
                                    className="bg-[#C84B31] text-base shadow-[0_6px_20px_rgba(200,75,49,0.28)] hover:bg-[#A33923]"
                                >
                                    Start {trialDays}-day free trial
                                </Button>
                            </Link>
                            <a href={bookingUrl("demo-salon")} data-testid="hero-cta-demo">
                                <Button
                                    size="lg"
                                    variant="outline"
                                    className="border-[#4A0E17]/20 bg-white/70 text-base text-[#4A0E17] hover:border-[#D4AF37]/45 hover:bg-[#FFFEF9]"
                                >
                                    Book a demo
                                </Button>
                            </a>
                        </div>
                        <div className="mt-8 flex flex-wrap items-center gap-6 text-sm font-medium text-[#8A8178]">
                            {["No credit card required", "Easy setup in minutes", "Cancel anytime, no lock-in"].map((t) => (
                                <div key={t} className="flex items-center gap-2">
                                    <span className="text-[8px] leading-none text-[#D4AF37]">◆</span>
                                    {t}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="md:col-span-5">
                        <HeroImageCollage />
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className={`border-t border-[#E8E4DE]/60 bg-[#FAF9F6] ${SECTION_Y}`}>
                <div className={`mx-auto max-w-7xl ${PAGE_X}`}>
                    <SectionHeader
                        eyebrow="Everything in one place"
                        title={<>Stop juggling six apps. <span className="italic text-[#C84B31]">Run one salon.</span></>}
                    />
                    <div className={`grid ${CARD_GAP} sm:grid-cols-2 lg:grid-cols-3 ${BLOCK_GAP}`}>
                        {features.map((f) => (
                            <FeatureCard
                                key={f.t}
                                icon={f.i}
                                title={f.t}
                                description={f.d}
                                tone={f.tone}
                                wash={f.wash}
                            />
                        ))}
                    </div>
                </div>
            </section>

            <SalonValueSection />

            {/* How it works */}
            <section
                id="how"
                className="py-12 sm:py-14"
                style={{ background: "linear-gradient(165deg, #4A0E17 0%, #3a0b12 100%)" }}
            >
                <div className={`mx-auto max-w-7xl ${PAGE_X}`}>
                    <div className={`grid items-center ${CARD_GAP} lg:grid-cols-2 lg:gap-8`}>
                        <div className="order-2 lg:order-1">
                            <HowSideImage />
                        </div>
                        <div className="order-1 lg:order-2">
                            <SectionHeader
                                light
                                eyebrow="From paper to platform"
                                title={<>Live in <em className="text-[#D4AF37] not-italic">15 minutes.</em></>}
                            />
                            <ol className="mt-5">
                                {howSteps.map((step, i) => (
                                    <HowStepRow
                                        key={step.title}
                                        index={i + 1}
                                        title={step.title}
                                        description={step.description}
                                        isLast={i === howSteps.length - 1}
                                    />
                                ))}
                            </ol>
                        </div>
                    </div>
                </div>
            </section>

            <TestimonialSection />

            {/* Pricing */}
            <section id="pricing" className={`${SECTION_Y} bg-[#FAF9F6]`}>
                <div className={`mx-auto max-w-5xl ${PAGE_X} text-center`}>
                    <SectionHeader
                        center
                        eyebrow="Honest pricing"
                        title="Simple, honest pricing"
                        subtitle="Start free — upgrade when your salon is ready."
                    />
                    <div className="mt-6 inline-flex items-center gap-1 rounded-full border border-[#E8E4DE] bg-white p-1 shadow-sm">
                        <button
                            type="button"
                            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                                cycle === "monthly"
                                    ? "bg-[#4A0E17] text-white"
                                    : "text-[#8A8178] hover:text-[#4A0E17]"
                            }`}
                            onClick={() => setCycle("monthly")}
                        >
                            Monthly
                        </button>
                        <button
                            type="button"
                            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                                cycle === "yearly"
                                    ? "bg-[#4A0E17] text-white"
                                    : "text-[#8A8178] hover:text-[#4A0E17]"
                            }`}
                            onClick={() => setCycle("yearly")}
                        >
                            Yearly
                        </button>
                    </div>
                    <div className={`grid items-stretch ${CARD_GAP} text-left ${BLOCK_GAP} ${plans.length <= 2 ? "mx-auto max-w-3xl md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3"}`}>
                    {plansLoading && (
                        <>
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="rounded-xl border border-[#E8E4DE] bg-white p-5 animate-pulse">
                                    <div className="h-5 w-24 rounded bg-[#F2EFE9]" />
                                    <div className="mt-3 h-9 w-28 rounded bg-[#F2EFE9]" />
                                    <div className="mt-5 space-y-2">
                                        <div className="h-3 w-full rounded bg-[#F2EFE9]" />
                                        <div className="h-3 w-4/5 rounded bg-[#F2EFE9]" />
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                    {!plansLoading && plans.map((p) => {
                        const featured = !!p.highlighted;
                        const price = cycle === "yearly" ? p.price_yearly : p.price_monthly;
                        const perks = (p.features?.length ? p.features : [
                            `Up to ${p.max_staff} staff`,
                            `${p.max_outlets} outlet${p.max_outlets > 1 ? "s" : ""}`,
                            p.trial_days ? `${p.trial_days}-day free trial` : null,
                        ]).filter(Boolean);
                        return (
                        <div
                            key={p.id}
                            className={`relative flex h-full flex-col overflow-hidden rounded-xl border p-5 ${
                                featured
                                    ? "border-[#C84B31] bg-[#4A0E17] text-white shadow-[0_12px_32px_rgba(74,14,23,0.2)]"
                                    : "border-[#E8E4DE] bg-white shadow-[0_4px_18px_rgba(45,41,38,0.04)]"
                            }`}
                        >
                            {featured && (
                                <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-[#D4AF37] via-[#C84B31] to-[#D4AF37]" aria-hidden />
                            )}
                            <div className="flex items-center justify-between gap-2 font-serif text-xl">
                                <span className={featured ? "text-white" : "text-[#4A0E17]"}>{p.name}</span>
                                {featured && (
                                    <span className="rounded-full bg-white/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide">
                                        Popular
                                    </span>
                                )}
                            </div>
                            {p.description && (
                                <p className={`mt-1 min-h-[2.5rem] text-sm leading-snug ${featured ? "text-white/75" : "text-[#8A8178]"}`}>
                                    {p.description}
                                </p>
                            )}
                            <div className={`mt-3 font-serif text-4xl font-medium ${featured ? "text-white" : "text-[#4A0E17]"}`}>
                                {inr(price)}
                            </div>
                            <div className={`mt-1 min-h-[2rem] text-xs ${featured ? "text-white/70" : "text-[#8A8178]"}`}>
                                per {cycle === "yearly" ? "year" : "month"}
                                {cycle === "yearly" && p.price_monthly > 0 && (
                                    <span className="mt-0.5 block">≈ {inr(Math.round(p.price_yearly / 12))}/mo</span>
                                )}
                                {cycle === "monthly" && p.price_yearly > 0 && (
                                    <span className="mt-0.5 block">{inr(p.price_yearly)}/year billed annually</span>
                                )}
                            </div>
                            <ul className={`mt-5 flex-1 space-y-1.5 text-sm ${featured ? "text-white/88" : "text-[#5C4033]/85"}`}>
                                {perks.map((x) => (
                                    <li key={x} className="flex gap-2">
                                        <span className={featured ? "text-[#D4AF37]" : "text-[#C9A227]"}>◆</span>
                                        {x}
                                    </li>
                                ))}
                            </ul>
                            <Link to={`/register?plan=${p.slug}&cycle=${cycle}`} className="mt-auto block pt-5">
                                <Button
                                    className={`h-10 w-full font-semibold ${
                                        featured
                                            ? "bg-white text-[#4A0E17] hover:bg-[#FFFEF9]"
                                            : "bg-[#4A0E17] text-white hover:bg-[#3b0b12]"
                                    }`}
                                    data-testid={`pricing-cta-${p.slug}`}
                                >
                                    Start free trial
                                </Button>
                            </Link>
                        </div>
                        );
                    })}
                    {!plansLoading && plans.length === 0 && (
                        <p className="col-span-full text-center text-sm text-[#8A8178]">
                            Pricing plans coming soon — contact us to get started.
                        </p>
                    )}
                </div>
                </div>
            </section>

            <LandingFooter />
        </div>
    );
}
